import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import TargetMenuModal from 'components/TargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import { TARGET_MENU_SHABU_SUKIYAKI } from 'utils/constants';
import { Genre } from '../models';
import { shabuSukiyakiService } from '../services';
import './style.scss';

function ShabuSukiyaki() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const BASE_HEIGHT_SELECTION = 30;
  const SHABU_TYPE = 'shabu';
  const SUKIYAKI_TYPE = 'sukiyaki';
  const DASHI_TYPE = 'dashi';
  const GENRE_TYPE = 'genre';

  // refs
  const selectFormGenreRef = useRef<HTMLSelectElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [dropdownHeight, setDropdownHeight] = useState(BASE_HEIGHT_SELECTION);
  const [isShowShabuModal, setIsShowShabuModal] = useState(false);
  const [isShowSukiyakiModal, setIsShowSukiyakiModal] = useState(false);
  const [isShowDashiModal, setIsShowDashiModal] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const { height } = entries[0].contentRect;
        const extraHeight = height > BASE_HEIGHT_SELECTION ? height - BASE_HEIGHT_SELECTION : 0;
        setDropdownHeight(BASE_HEIGHT_SELECTION + extraHeight + 20);
      }
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [BASE_HEIGHT_SELECTION]);

  useEffect(() => {
    handleLoadGenresData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLoadGenresData() {
    (async () => {
      try {
        const response = await shabuSukiyakiService.getList(GENRE_TYPE);
        const all = (response.data as { [key: string]: any })['all'];
        const available = (response.data as { [key: string]: any })['available'];
        setGenres(all);
        setSelectedGenres(available.map((genre: Genre) => genre.id));
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleAddTargetMenuShabuModal() {
    setIsShowShabuModal(true);
  }
  function handleCloseTargetMenuShabuModal() {
    setIsShowShabuModal(false);
  }
  function handleAddTargetMenuSukiyakiModal() {
    setIsShowSukiyakiModal(true);
  }
  function handleCloseTargetMenuSukiyakiModal() {
    setIsShowSukiyakiModal(false);
  }
  function handleAddTargetMenuDashiModal() {
    setIsShowDashiModal(true);
  }
  function handleCloseTargetMenuDashiModal() {
    setIsShowDashiModal(false);
  }

  function handleSubmit() {
    setIsSubmitted(true);
    if (selectedGenres.length === 0) {
      selectFormGenreRef.current?.focus();
      return;
    }
    (async () => {
      try {
        await shabuSukiyakiService.createOrUpdateDashiGenres(selectedGenres, GENRE_TYPE);
        showGlobalModal(() => <AlertModal title={t('App.info')} message={t('ShabuSukiyaki.save_success')} />);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();

    setIsSubmitted(false);
  }

  function handleChangeGenreOption(event: any) {
    const selectedValue = parseInt(event.target.value);
    setSelectedGenres((prevSelected) => {
      const newSelections = prevSelected.includes(selectedValue)
        ? prevSelected.filter((id) => id !== selectedValue)
        : [...prevSelected, selectedValue];
      return newSelections;
    });
    setIsSubmitted(false);
  }

  function removeGenre(id: number) {
    setSelectedGenres((prevSelected) => prevSelected.filter((genreId) => genreId !== id));
    setIsSubmitted(false);
  }

  function handleLoadShabu() {
    return shabuSukiyakiService.getList(SHABU_TYPE);
  }
  function handleAddShabu(menuIds: number[]) {
    return shabuSukiyakiService.addMenuIds(menuIds, SHABU_TYPE);
  }
  function handleDeleteShabu(menuIds: number[]) {
    return shabuSukiyakiService.deleteMenuIds(menuIds, SHABU_TYPE);
  }
  function handleLoadSukiyaki() {
    return shabuSukiyakiService.getList(SUKIYAKI_TYPE);
  }
  function handleAddSukiyaki(menuIds: number[]) {
    return shabuSukiyakiService.addMenuIds(menuIds, SUKIYAKI_TYPE);
  }
  function handleDeleteSukiyaki(menuIds: number[]) {
    return shabuSukiyakiService.deleteMenuIds(menuIds, SUKIYAKI_TYPE);
  }
  function handleLoadDashi() {
    return shabuSukiyakiService.getList(DASHI_TYPE);
  }
  function handleAddDashi(menuIds: number[]) {
    return shabuSukiyakiService.addMenuIds(menuIds, DASHI_TYPE);
  }
  function handleDeleteDashi(menuIds: number[]) {
    return shabuSukiyakiService.deleteMenuIds(menuIds, DASHI_TYPE);
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('ShabuSukiyaki.title')} />
          </div>
          <div className="m-3">{t('ShabuSukiyaki.description')}</div>
          <div className="d-flex flex-column justify-content-center align-items-center ">
            <div className="text-end" style={{ width: '700px', marginTop: '20px' }}>
              <Button className="btn btn--dark-green" onClick={handleSubmit}>
                {t('ShabuSukiyaki.save_button')}
              </Button>
            </div>
            <div className="sticky-table" style={{ width: '700px', marginTop: '20px' }}>
              <Table bordered style={{ borderTop: '1px solid #dee2e6' }}>
                <tbody>
                  <tr style={{ height: '50px' }}>
                    <td className="text-center" style={{ width: '40%', backgroundColor: '#36489a', color: '#fff' }}>
                      {t('ShabuSukiyaki.shabu')}
                    </td>
                    <td className="text-center" style={{ width: '20%', backgroundColor: '#36489a', color: '#fff' }}>
                      {t('ShabuSukiyaki.menu')}
                    </td>
                    <td className="text-center">
                      <Button style={{ minWidth: '90px' }} onClick={handleAddTargetMenuShabuModal}>
                        {t('ShabuSukiyaki.target_menu_button')}
                      </Button>
                    </td>
                  </tr>
                  <tr style={{ height: '50px' }}>
                    <td className="text-center" style={{ width: '40%', backgroundColor: '#36489a', color: '#fff' }}>
                      {t('ShabuSukiyaki.sukiyaki')}
                    </td>
                    <td className="text-center" style={{ width: '20%', backgroundColor: '#36489a', color: '#fff' }}>
                      {t('ShabuSukiyaki.menu')}
                    </td>
                    <td className="text-center">
                      <Button style={{ minWidth: '90px' }} onClick={handleAddTargetMenuSukiyakiModal}>
                        {t('ShabuSukiyaki.target_menu_button')}
                      </Button>
                    </td>
                  </tr>
                  <tr style={{ height: '100px' }}>
                    <th rowSpan={2} colSpan={1}>
                      {t('ShabuSukiyaki.dashi')}
                    </th>
                    <th>{t('ShabuSukiyaki.genre')}</th>
                    <td className="text-center">
                      <div style={{ position: 'relative' }}>
                        {/* Container for selected genre tags with callback ref */}
                        <div ref={containerRef} className="container-tag" style={{ maxHeight: '200px' }}>
                          {selectedGenres.map((genreId) => {
                            const genre = genres.find((g: Genre) => g.id === genreId);
                            return (
                              <span key={genreId} className="genre-tag">
                                {genre?.name}
                                <button onClick={() => removeGenre(genreId)}>x</button>
                              </span>
                            );
                          })}
                        </div>
                        {/* Multi-Select Dropdown */}
                        <Form.Select
                          ref={selectFormGenreRef}
                          as="select"
                          className="text-center"
                          onChange={handleChangeGenreOption}
                          value={selectedGenres.length > 0 ? '' : undefined}
                          style={{
                            minHeight: `${dropdownHeight}px`,
                            minWidth: '280px',
                            margin: '10px auto 0',
                            position: 'relative',
                            zIndex: 1,
                          }}
                        >
                          <option value="" hidden={selectedGenres.length > 0}>
                            {selectedGenres.length === 0 ? t('ShabuSukiyaki.no_select') : ''}
                          </option>
                          {genres
                            .filter((genre: any) => !selectedGenres.includes(genre.id))
                            .map((genre: any) => (
                              <option key={genre.id} value={genre.id}>
                                {genre.name.length > 20 ? genre.name.slice(0, 30) + '...' : genre.name}
                              </option>
                            ))}
                        </Form.Select>
                      </div>
                      {isSubmitted && selectedGenres.length === 0 && (
                        <span className="small text-danger d-inline-block mt-1">
                          {t('ShabuSukiyaki.no_genre_required')}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr style={{ height: '50px' }}>
                    <th>{t('ShabuSukiyaki.menu')}</th>
                    <td className="text-center">
                      <Button style={{ minWidth: '90px' }} onClick={handleAddTargetMenuDashiModal}>
                        {t('ShabuSukiyaki.target_menu_button')}
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
        {isShowShabuModal &&
          createPortal(
            <TargetMenuModal
              targetName={t('ShabuSukiyaki.shabu')}
              targetSourceType={TARGET_MENU_SHABU_SUKIYAKI}
              onLoadData={handleLoadShabu}
              onAdd={handleAddShabu}
              onDelete={handleDeleteShabu}
              onClose={handleCloseTargetMenuShabuModal}
            />,
            document.body
          )}
        {isShowSukiyakiModal &&
          createPortal(
            <TargetMenuModal
              targetName={t('ShabuSukiyaki.sukiyaki')}
              targetSourceType={TARGET_MENU_SHABU_SUKIYAKI}
              onLoadData={handleLoadSukiyaki}
              onAdd={handleAddSukiyaki}
              onDelete={handleDeleteSukiyaki}
              onClose={handleCloseTargetMenuSukiyakiModal}
            />,
            document.body
          )}
        {isShowDashiModal &&
          createPortal(
            <TargetMenuModal
              targetName={t('ShabuSukiyaki.dashi')}
              targetSourceType={TARGET_MENU_SHABU_SUKIYAKI}
              onLoadData={handleLoadDashi}
              onAdd={handleAddDashi}
              onDelete={handleDeleteDashi}
              onClose={handleCloseTargetMenuDashiModal}
            />,
            document.body
          )}
      </div>
    </div>
  );
}

export default ShabuSukiyaki;
